import moment from "moment";

// Shapes the saved caries-risk evaluation into the rows a printable A4 sheet
// needs. The on-screen modal shows every radio option; the printout shows only
// the answer that was picked, so 13 questions + 2 section headers fit one page.

export const RISK_LABELS = {
  low: "ต่ำ",
  medium: "ปานกลาง",
  high: "สูง",
};

// Backend stores evaluationScore as 1/2/3; the modal computes the same ranking
// from the raw answers. Keep both readable through one map.
export const RISK_SCORES = {
  1: "low",
  2: "medium",
  3: "high",
};

export function riskLabel(risk) {
  return RISK_LABELS[risk] || "-";
}

// Worst answer wins: any "high" makes the patient high risk, otherwise any
// "medium" makes them medium. Mirrors the modal's evaluationScore getter.
export function overallRisk(answers) {
  const values = Object.values(answers || {});
  if (values.includes("high")) return "high";
  if (values.includes("medium")) return "medium";
  if (values.length === 0) return null;
  return "low";
}

// Flatten questions into print rows, carrying the section header each question
// sits under so the sheet can group them. `answers` is keyed by question id.
export function buildReportRows(questions, answers) {
  const rows = [];
  let section = null;

  for (const q of questions || []) {
    if (q.type === "header") {
      section = q.text;
      rows.push({ type: "header", text: q.text });
      continue;
    }

    const value = (answers || {})[q.id];
    rows.push({
      type: "question",
      id: q.id,
      section,
      text: q.text,
      // The chosen option's own wording ("ใช่" / "ไม่" / a long clause), not the
      // risk level — the level goes in its own column.
      answerText: (q.answer && q.answer[value]) || "-",
      risk: value || null,
      riskText: value ? riskLabel(value) : "-",
    });
  }

  return rows;
}

// Thai Buddhist-era date, matching the other printable docs (MedicalCert).
export function formatThaiDate(date) {
  if (!date) return "-";
  const m = moment(date);
  if (!m.isValid()) return "-";
  return m.add(543, "years").locale("th").format("D MMMM YYYY");
}

export default function cariesRiskReport({
  questions,
  answers,
  creationDt,
  modifyDt,
}) {
  const risk = overallRisk(answers);
  return {
    rows: buildReportRows(questions, answers),
    overallRisk: risk,
    overallRiskText: risk ? riskLabel(risk) : "-",
    assessedDate: formatThaiDate(creationDt),
    // Records that were never edited still carry a modifyDt equal to creationDt;
    // showing it is what the clinic asked for, so pass it through as-is.
    modifiedDate: formatThaiDate(modifyDt || creationDt),
  };
}
